import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Pencil, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const EditDomainDialog = ({ domain, onDomainUpdated }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    tags: '',
    notes: '',
    renewal_link: '',
    manual_expiration_date: ''
  });

  useEffect(() => {
    if (domain && open) {
      // Convert expiration_date to YYYY-MM-DD format for date input
      let expirationDateValue = '';
      if (domain.expiration_date) {
        const date = new Date(domain.expiration_date);
        expirationDateValue = date.toISOString().split('T')[0];
      }

      setFormData({
        tags: domain.tags?.join(', ') || '',
        notes: domain.notes || '',
        renewal_link: domain.renewal_link || '',
        manual_expiration_date: expirationDateValue
      });
    }
  }, [domain, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
        notes: formData.notes,
        renewal_link: formData.renewal_link || null,
        manual_expiration_date: formData.manual_expiration_date || null
      };

      await axios.put(`${API_URL}/domains/${domain.id}`, payload);
      toast.success('Domain updated successfully!');
      setOpen(false);
      onDomainUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update domain');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid={`edit-button-${domain?.id}`}>
          <Pencil className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="edit-domain-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Edit Domain</DialogTitle>
          <DialogDescription className="font-body">
            Update details for {domain?.domain_name}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="edit-expiration" className="text-xs font-mono uppercase tracking-widest">
              Expiration Date
            </Label>
            <Input
              id="edit-expiration"
              type="date"
              value={formData.manual_expiration_date}
              onChange={(e) => setFormData({ ...formData, manual_expiration_date: e.target.value })}
              disabled={loading}
              className="font-mono"
              data-testid="edit-expiration-date-input"
            />
            <p className="text-xs text-muted-foreground">
              Override WHOIS data or add manual expiration date
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-tags" className="text-xs font-mono uppercase tracking-widest">
              Tags (comma-separated)
            </Label>
            <Input
              id="edit-tags"
              placeholder="personal, client, important"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              disabled={loading}
              className="font-mono"
              data-testid="edit-tags-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-renewal" className="text-xs font-mono uppercase tracking-widest">
              Renewal Link
            </Label>
            <Input
              id="edit-renewal"
              type="url"
              placeholder="https://registrar.com/renew"
              value={formData.renewal_link}
              onChange={(e) => setFormData({ ...formData, renewal_link: e.target.value })}
              disabled={loading}
              className="font-mono"
              data-testid="edit-renewal-link-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-notes" className="text-xs font-mono uppercase tracking-widest">
              Notes
            </Label>
            <Textarea
              id="edit-notes"
              placeholder="Optional notes about this domain"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              disabled={loading}
              className="font-body"
              data-testid="edit-notes-input"
            />
          </div>
          <Button
            type="submit"
            className="w-full"
            disabled={loading}
            data-testid="submit-edit-button"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Updating...
              </>
            ) : (
              'Update Domain'
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
